import {
  type CSSProperties,
  type ExtractPropTypes,
  type PropType,
  defineComponent,
} from 'vue'

// Utils
import {
  createNamespace,
  extend,
  isDef,
  makeObjectProp,
  makeStringProp,
  numericProp,
  truthProp,
  unknownProp,
} from '@xzx-design/utils'

// Composables
import { routeProps, useRoute } from '@xzx-design/hooks'

// Components
import { Icon } from '@xzx-design/components/icon'

import { Right } from '@xzx-design/icons-vue'
import type { IconsComponent } from '@xzx-design/components/icon'

const [name, bem] = createNamespace('cell')

export type CellSize = 'normal' | 'large'

export type CellArrowDirection = 'up' | 'down' | 'left' | 'right'

export const cellSharedProps = {
  tag: makeStringProp<keyof HTMLElementTagNameMap>('div'),
  icon: makeObjectProp<IconsComponent>(null),
  size: String as PropType<CellSize>,
  title: numericProp,
  value: numericProp,
  label: numericProp,
  center: Boolean,
  isLink: Boolean,
  disabled: Boolean,
  border: truthProp,
  iconPrefix: String,
  valueClass: unknownProp,
  labelClass: unknownProp,
  titleClass: unknownProp,
  titleStyle: null as unknown as PropType<string | CSSProperties>,
  arrowDirection: String as PropType<CellArrowDirection>,
  required: {
    type: [Boolean, String] as PropType<boolean | 'auto'>,
    default: null,
  },
  clickable: {
    type: Boolean as PropType<boolean | null>,
    default: null,
  },
}

export const cellProps = extend({}, cellSharedProps, routeProps)

export type CellProps = ExtractPropTypes<typeof cellProps>

export default defineComponent({
  name,

  props: cellProps,

  setup(props, { slots }) {
    const route = useRoute()

    const renderLabel = () => {
      const showLabel = slots.label || isDef(props.label)

      if (showLabel) {
        return (
          <div class={[bem('label'), props.labelClass]}>
            {slots.label ? slots.label() : props.label}
          </div>
        )
      }
    }

    const renderTitle = () => {
      if (slots.title || isDef(props.title)) {
        const titleSlot = slots.title?.()

        // Allow Field to dynamically set empty label
        // https://github.com/youzan/vant/issues/11368
        if (Array.isArray(titleSlot) && titleSlot.length === 0) {
          return
        }

        return (
          <div
            class={[bem('title'), props.titleClass]}
            style={props.titleStyle}
          >
            {titleSlot || <span>{props.title}</span>}
            {renderLabel()}
          </div>
        )
      }
    }

    const renderValue = () => {
      // slots.default is an alias of slots.value
      const slot = slots.value || slots.default
      const hasValue = slot || isDef(props.value)

      if (hasValue) {
        return (
          <div class={[bem('value'), props.valueClass]}>
            {slot ? slot() : <span>{props.value}</span>}
          </div>
        )
      }
    }

    const renderLeftIcon = () => {
      if (slots.icon) {
        return slots.icon()
      }

      if (props.icon) {
        return (
          <Icon class={bem('left-icon')} classPrefix={props.iconPrefix}>
            <props.icon />
          </Icon>
        )
      }
    }

    const renderRightIcon = () => {
      if (slots['right-icon']) {
        return slots['right-icon']()
      }

      if (props.isLink) {
        const IconSvg = Right
        return (
          <Icon class={bem('right-icon')}>
            <IconSvg />
          </Icon>
        )
      }
    }

    return () => {
      const {
        tag: Tag,
        size,
        center,
        border,
        isLink,
        required,
        disabled,
      } = props
      const clickable = props.clickable ?? isLink

      const classes: Record<string, boolean | undefined> = {
        center,
        required: !!required,
        clickable,
        borderless: !border,
        disabled,
      }
      if (size) {
        classes[size] = !!size
      }

      return (
        <Tag
          class={bem(classes)}
          role={clickable ? 'button' : undefined}
          tabindex={clickable ? 0 : undefined}
          onClick={route}
        >
          {renderLeftIcon()}
          {renderTitle()}
          {renderValue()}
          {renderRightIcon()}
          {slots.extra?.()}
        </Tag>
      )
    }
  },
})
